import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2, Paperclip, Wallet } from 'lucide-react'
import { useRef } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { NumberInput } from '@/components/ui/number-input'
import { FormSelect } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { Field, FormSection } from '../../components/FormBits'
import { toastError } from '../../ui'
import { usePersonalWallets, useSaveEntry } from '../api'
import { PERSONAL_EXPENSE_CATEGORIES, PERSONAL_INCOME_CATEGORIES, RECURRENCES, type PersonalEntry } from '../types'

export function EntryForm({ entry, onDone }: { entry: PersonalEntry | null; onDone: () => void }) {
  const { t } = useTranslation()
  const save = useSaveEntry()
  const wallets = usePersonalWallets()
  const fileRef = useRef<HTMLInputElement>(null)
  const cats = [...PERSONAL_EXPENSE_CATEGORIES, ...PERSONAL_INCOME_CATEGORIES] as const

  const schema = z.object({
    kind: z.enum(['expense', 'income']),
    category: z.enum(cats),
    amount: z.string().regex(/^\d+(\.\d{1,2})?$/, t('finance.form.amountInvalid')).refine((v) => Number(v) > 0, t('finance.form.amountInvalid')),
    date: z.string().min(1, t('finance.form.dateRequired')),
    walletId: z.string().min(1, t('finance.personal.form.walletRequired')),
    recurrence: z.enum(RECURRENCES),
    status: z.enum(['paid', 'pending']),
    description: z.string().trim().max(160),
    attachmentName: z.string(),
  })
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      kind: entry?.kind ?? 'expense',
      category: entry?.category ?? 'market',
      amount: entry?.amount ?? '',
      date: entry?.date ?? new Date().toISOString().slice(0, 10),
      walletId: entry?.walletId ?? wallets.data?.[0]?.id ?? '',
      recurrence: entry?.recurrence ?? 'once',
      status: entry?.status ?? 'paid',
      description: entry?.description ?? '',
      attachmentName: entry?.attachmentName ?? '',
    },
  })
  const { errors } = form.formState
  const [kind, attachmentName] = useWatch({ control: form.control, name: ['kind', 'attachmentName'] })
  const catOptions = (kind === 'income' ? PERSONAL_INCOME_CATEGORIES : PERSONAL_EXPENSE_CATEGORIES).map((c) => ({ value: c, label: t(`finance.personal.category.${c}`) }))

  const onSubmit = form.handleSubmit(async (input) => {
    try {
      await save.mutateAsync({ id: entry?.id, input })
      toast.success(t(entry ? 'finance.personal.entryUpdated' : 'finance.personal.entryCreated'))
      onDone()
    } catch (e) {
      toastError(e)
    }
  })

  return (
    <Modal
      open
      size="lg"
      onOpenChange={(v) => !v && onDone()}
      dismissible={!save.isPending}
      icon={<Wallet />}
      title={t(entry ? 'finance.personal.form.editEntry' : 'finance.personal.form.createEntry')}
      description={t('finance.personal.privateHint')}
      onSubmit={(e) => void onSubmit(e)}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onDone}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <Controller
          control={form.control}
          name="kind"
          render={({ field }) => (
            <div className="grid grid-cols-2 rounded-lg border bg-muted/40 p-1" role="radiogroup" aria-label={t('finance.tx.form.kind')}>
              {(['expense', 'income'] as const).map((k) => (
                <button
                  key={k}
                  type="button"
                  role="radio"
                  aria-checked={field.value === k}
                  onClick={() => {
                    field.onChange(k)
                    form.setValue('category', k === 'income' ? 'salary' : 'market')
                  }}
                  className={cn('rounded-md py-1.5 text-sm font-medium transition-colors', field.value === k ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}
                >
                  {t(`finance.personal.kind.${k}`)}
                </button>
              ))}
            </div>
          )}
        />
        <FormSection>
          <Field id="pe-cat" label={t('finance.category')}>
            <FormSelect id="pe-cat" control={form.control} name="category" options={catOptions} />
          </Field>
          <Field id="pe-amount" label={t('finance.amount')} error={errors.amount?.message}>
            <Controller control={form.control} name="amount" render={({ field }) => <NumberInput id="pe-amount" value={field.value} onChange={field.onChange} onBlur={field.onBlur} minDecimalsOnBlur={2} suffix="₺" aria-invalid={!!errors.amount} />} />
          </Field>
          <Field id="pe-date" label={t('finance.entry.date')} error={errors.date?.message}>
            <Controller control={form.control} name="date" render={({ field }) => <DateField id="pe-date" value={field.value} onChange={field.onChange} clearable={false} />} />
          </Field>
          <Field id="pe-wallet" label={t('finance.personal.wallet')} error={errors.walletId?.message}>
            <FormSelect id="pe-wallet" control={form.control} name="walletId" placeholder={t('common.select')} options={(wallets.data ?? []).map((w) => ({ value: w.id, label: w.name, description: w.last4 ? `•••• ${w.last4}` : undefined }))} />
          </Field>
          <Field id="pe-rec" label={t('finance.personal.recurrence')}>
            <FormSelect id="pe-rec" control={form.control} name="recurrence" options={RECURRENCES.map((r) => ({ value: r, label: t(`finance.personal.recurrenceOpt.${r}`) }))} />
          </Field>
          <Field id="pe-status" label={t(kind === 'income' ? 'finance.personal.statusIn' : 'finance.personal.statusOut')}>
            <FormSelect id="pe-status" control={form.control} name="status" options={(['paid', 'pending'] as const).map((s) => ({ value: s, label: t(`finance.personal.status.${kind}.${s}`) }))} />
          </Field>
          <Field id="pe-desc" label={t('finance.tx.description')} full>
            <Input id="pe-desc" maxLength={160} {...form.register('description')} />
          </Field>
          <div className="flex items-center gap-3 rounded-lg border border-dashed p-3 sm:col-span-2">
            <Paperclip className="size-5 shrink-0 text-muted-foreground" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">{t('finance.personal.form.attachment')}</p>
              <p className="truncate text-xs text-muted-foreground">{attachmentName || t('finance.personal.form.attachmentHint')}</p>
            </div>
            <input ref={fileRef} type="file" accept="application/pdf,image/*" className="sr-only" tabIndex={-1} aria-hidden onChange={(e) => form.setValue('attachmentName', e.target.files?.[0]?.name ?? '')} />
            <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()}>
              {t('finance.pay.form.pickFile')}
            </Button>
          </div>
        </FormSection>
      </div>
    </Modal>
  )
}
